import React, { useState } from 'react';

import capitalize from 'lodash/capitalize';
import PropTypes from 'prop-types';
import { faCheck } from '@awesome.me/kit-a6e51d954f/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import iconShape from '../../../../types/icon';

const pluralize = (word) => {
  if (word.endsWith('y')) return `${word.slice(0, -1)}ies`;
  if (word.endsWith('s')) return `${word}es`;

  return `${word}s`;
};

const SelectChip = ({ control, entity, icon, name, options, disabled = false }) => {
  // <Popover />s automatically open and close when the trigger is selected or when
  // clicking outside the component. However, a <Popover /> will not automatically close
  // when a <CommandItem /> is selected. In this case, open state must be manually
  // managed.
  const [open, setOpen] = useState(false);

  const entityLabel = capitalize(entity);
  const entityPlural = pluralize(entity);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selectedOption = options.find((option) => option.value === field.value);

        const buttonStyles = cn(
          'max-w-28 gap-1.5 text-xs data-[state=open]:bg-accent',
          'data-[state=open]:text-accent-foreground',
          selectedOption ? 'text-accent-foreground' : 'text-muted-foreground'
        );

        const handleSelect = (value) => {
          field.onChange(value);
          setOpen(false);
        };

        return (
          <FormItem>
            <FormLabel className="sr-only">{entityLabel}</FormLabel>

            <Popover onOpenChange={setOpen} open={open}>
              <FormControl>
                <PopoverTrigger asChild disabled={disabled}>
                  <Button className={buttonStyles} variant="outline">
                    {selectedOption ? (
                      <>
                        {selectedOption.icon ?? icon}
                        <span className="truncate">{selectedOption.label}</span>
                      </>
                    ) : (
                      <>
                        {icon} {entityLabel}
                      </>
                    )}
                  </Button>
                </PopoverTrigger>
              </FormControl>

              <PopoverContent align="start" className="w-52 p-0">
                <Command className="[&_[cmdk-input-wrapper]]:h-8 [&_[cmdk-item]]:py-1">
                  <CommandInput placeholder={`Search ${entityPlural}...`} />

                  <CommandList>
                    <CommandEmpty>{`No ${entityPlural} found.`}</CommandEmpty>

                    <CommandGroup>
                      {options.map((option) => {
                        const isSelected = field.value === option.value;

                        return (
                          <CommandItem
                            key={option.value}
                            onSelect={() => handleSelect(option.value)}
                            value={option.label}
                          >
                            {option.icon}
                            <span className="truncate">{option.label}</span>

                            {isSelected && (
                              <FontAwesomeIcon className="ml-auto" icon={faCheck} />
                            )}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

const optionShape = PropTypes.shape({
  icon: PropTypes.oneOfType([PropTypes.element, iconShape]),
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
});

SelectChip.propTypes = {
  control: PropTypes.shape({}).isRequired,
  entity: PropTypes.string.isRequired,
  icon: iconShape.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(optionShape).isRequired,
  disabled: PropTypes.bool,
};

export default SelectChip;
